import React, { useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions, Platform, Linking } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Input } from '@/components/Input';
import { PasswordInput } from '@/components/PasswordInput';
import { Button } from '@/components/Button';
import { ErrorMessage } from '@/components/ErrorMessage';
import { Logo } from '@/components/Logo';
import { AuthFooter } from '@/components/AuthFooter';
import { colors } from '@/styles/colors';
import { ROUTES, ERROR_MESSAGES } from '@/utils/constants';
import { validateEmail, validatePassword } from '@/services/validationService';
import { handleLogin } from '@/services/authService';

interface LoginFormProps {
  onSSOClick: () => void;
  onTwoFactorRequired: (email: string, password: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSSOClick, onTwoFactorRequired }) => {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const params = useLocalSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const nextUrl = typeof params.next === 'string' ? params.next : undefined;
  const signInError = params.sign_in_error === 'true';
  const showSSO = process.env.EXPO_PUBLIC_SSO_ENABLED === 'true';
  const isDesktop = width >= 768;

  const handleSubmit = async () => {
    setEmailError(null);
    setPasswordError(null);
    setError(null);

    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    if (emailValidation || passwordValidation) {
      setEmailError(emailValidation);
      setPasswordError(passwordValidation);
      return;
    }

    setLoading(true);

    try {
      const result = await handleLogin({ email, password }, nextUrl);

      if (result.requiresTwoFactor) {
        onTwoFactorRequired(email, password);
      } else {
        if (Platform.OS === 'web') {
         window.location.href = '/app/';
        } else {
          Linking.openURL('/app/');
        }
      }
    } catch (err: any) {
      setError(ERROR_MESSAGES.INVALID_CREDENTIALS);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.formContainer}>
      <Logo />
      
      <View style={styles.formContent}>
        <Text style={styles.heading}>Welcome back</Text>
        <Text style={[styles.subheading, isDesktop && styles.subheadingDesktop]}>
          Let's get you logged in to Vida Health
        </Text>

        {signInError && (
          <ErrorMessage 
            message="The link you have used is either expired or invalid. Please log in using your email and password"
          />
        )}

        {error && <ErrorMessage message={error} />}

        <Input
          label="Email"
          value={email}
          onChangeText={setEmail}
          placeholder="Email or username"
          keyboardType="email-address"
          autoComplete="email"
          maxLength={254}
          error={emailError || undefined}
          testID="email_input"
        />

        <PasswordInput
          label="Password"
          value={password}
          onChangeText={setPassword}
          placeholder="Password"
          autoComplete="current-password"
          error={passwordError || undefined}
          testID="password_input"
          onSubmitEditing={handleSubmit}
        />

        <View style={isDesktop && styles.buttonContainer}>
          <Button
            title="Sign in"
            onPress={handleSubmit}
            loading={loading}
            testID="sign_in_button"
          />
        </View>

        <AuthFooter
          variant="login"
          onSSOClick={onSSOClick}
          showSSO={showSSO}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    width: '100%',
  },
  formContent: {
    width: '100%',
  },
  heading: {
    fontSize: 40,
    lineHeight: 48,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  subheading: {
    fontSize: 20,
    lineHeight: 28,
    color: colors.textSecondary,
    marginBottom: 32,
  },
  subheadingDesktop: {
    fontSize: 32,
    lineHeight: 45,
  },
  buttonContainer: {
    alignItems: 'flex-start',
  },
});
